'use client';
import React, { useRef, useEffect } from 'react';

export default function FinalCTA() {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('cta-active');
          }
        });
      },
      { threshold: 0.2 }
    );
    if (contentRef?.current) observer?.observe(contentRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="relative bg-[#060609] py-32 px-6 md:px-12 overflow-hidden">
      {/* Layered ambient glows */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.12) 0%, transparent 60%)', filter: 'blur(60px)' }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(236,72,153,0.08) 0%, transparent 60%)', filter: 'blur(40px)' }}
      />
      {/* Grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />
      <div
        ref={contentRef}
        className="cta-content relative z-10 max-w-4xl mx-auto text-center"
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-violet-500/20 bg-violet-500/5 mb-8">
          <div className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
          <span className="font-sans text-xs text-violet-300 tracking-wide">The Future Is Here</span>
        </div>

        {/* Headline */}
        <h2 className="font-sans font-bold text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] tracking-tight mb-6">
          The Future of Advertising{' '}
          <br className="hidden md:block" />
          Is{' '}
          <span className="relative">
            <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Creator-First.
            </span>
          </span>
        </h2>

        {/* Subtext */}
        <p className="font-sans text-xl text-white/40 max-w-2xl mx-auto leading-relaxed mb-12">
          The brands that win tomorrow are building their AI content systems today. Don&apos;t get left behind.
        </p>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://payhip.com/b/H2qt9"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center gap-3 px-10 py-5 rounded-full font-sans font-semibold text-lg text-white overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7, #ec4899)', boxShadow: '0 0 60px rgba(139,92,246,0.3)' }}
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: 'linear-gradient(135deg, #6d28d9, #9333ea, #db2777)', boxShadow: '0 0 80px rgba(139,92,246,0.5)' }}
            />
            <span className="relative z-10">Get AI UGC Studio™</span>
            <svg className="relative z-10 group-hover:translate-x-1 transition-transform duration-200" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10H16M11 5L16 10L11 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>

        {/* Final trust */}
        <div className="mt-10 flex items-center justify-center gap-8 flex-wrap">
          {[
            { icon: '⚡', text: 'Instant Access' },
            { icon: '🔒', text: 'Secure Payment' },
            { icon: '♾️', text: 'Lifetime Updates' },
            { icon: '✅', text: 'Commercial Rights' },
          ]?.map((item) => (
            <div key={item?.text} className="flex items-center gap-2">
              <span className="text-sm">{item?.icon}</span>
              <span className="font-sans text-sm text-white/30">{item?.text}</span>
            </div>
          ))}
        </div>
      </div>
      <style jsx>{`
        .cta-content {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 1s cubic-bezier(0.22, 1, 0.36, 1), transform 1s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .cta-active {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}
